import React from 'react';
import {
  Typography,
  Card,
  Box,
  Stack,
  LinearProgress,
  CardActionArea,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getSubjectColorNew,
  getSubjectIconName,
  getUrlConvertedString,
} from '../../../../helper';
import { TP_COLORS } from '../../../../constants';
import { getProgressV2State } from '../../../../store/V2/ProgressSlice';
import { useAppSelector } from '../../../../hooks';
import { ISubjectForHome } from '../../../../store/CurriculumSlice';

interface ISubjectForHomeExtended extends ISubjectForHome {
  curriculum: string;
}

interface SubjectProgressCardProps {
  subjectData: ISubjectForHomeExtended;
}

const SubjectProgressCard: React.FC<SubjectProgressCardProps> = ({
  subjectData,
}) => {
  // Store
  const { progress } = useAppSelector(getProgressV2State);

  const subjectProgress = progress.subjects.find(
    (item) =>
      item.subject === subjectData.name &&
      item.subjectCode === subjectData.subjectCode
  );

  const completedSubTopics = subjectProgress?.completedSubTopics || 0;

  const completionRate =
    subjectData.subTopicsCount && subjectData.subTopicsCount > 0
      ? (completedSubTopics / subjectData.subTopicsCount) * 100
      : 0;

  const subjectColor = getSubjectColorNew(subjectData.name);

  const curriculum = getUrlConvertedString(subjectData.curriculum);
  const subject = getUrlConvertedString(subjectData.name);
  const subjectLevel = getUrlConvertedString(subjectData.subjectLevel);
  const subjectCode = getUrlConvertedString(subjectData.subjectCode);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: '10px',
        border: '1px solid #E2E8F0',
      }}
    >
      <CardActionArea
        component={Link}
        to={`${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`}
        sx={{ padding: '16px' }}
      >
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box
            sx={{
              width: '33px',
              height: '33px',
              borderRadius: '7px',
              backgroundColor: `${subjectColor}33`,
            }}
          >
            <img
              src={`${
                process.env.PUBLIC_URL
              }/images/landingPage/icon-${getSubjectIconName(
                subjectData.name
              )}.avif`}
              alt="subject-img"
              style={{ height: '32px' }}
            />
          </Box>

          <Stack spacing={1} flex={1}>
            <Box>
              <Typography
                fontSize="14px"
                fontWeight="600"
                color={TP_COLORS.gray.darken}
                marginBottom="4px"
              >
                {subjectData.name}
              </Typography>
              <Typography fontSize="9px" color="#647488">
                #{subjectData.subjectCode} {subjectData.subjectLevel}
              </Typography>
            </Box>

            <Typography fontSize="14px" color="#94A3B8">
              {completedSubTopics}/{subjectData.subTopicsCount} Subtopics
            </Typography>

            <Box display="flex" justifyContent="space-between">
              <Typography fontSize="14px" color="#94A3B8">
                Progress
              </Typography>
              <Typography fontSize="14px" color={TP_COLORS.gray.darken}>
                {completionRate.toFixed()}%
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{
                backgroundColor: TP_COLORS.border.color1,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: subjectColor,
                },
              }}
            />
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default SubjectProgressCard;
